import { FlexCol, FlexRow, NumberBox } from '@/src/components'
import type { DatePickerType } from '@/src/components/DatePicker/datePicker.types'
import {
  EditableDataGrid,
  type EditableDataGridCellChange,
  type EditableDataGridColumn,
  type EditableDataGridRow,
} from '@/src/components/EditableDataGrid/EditableDataGrid'
import { FormDatePicker } from '@/src/components/Form/FormDatePicker'
import { PasteHintBadge } from '@/src/components/PasteHintBadge'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { Box, Flex, Grid, GridItem, Text } from '@chakra-ui/react'
import { endOfDay, endOfMonth, endOfWeek, startOfDay, startOfMonth, startOfWeek } from 'date-fns'
import type { FormikProps } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormInput } from '../../../components/Form/FormInput'
import { FormSelect } from '../../../components/Form/FormSelect'
import type { ICustomDataForm } from '../types/custom-data.interface'
import { getGranularityLabel } from '../utils'
import { useCustomTimeseriesData } from './hooks/useCustomTimeseriesData.hook'
import { generateCustomTimeseriesIntervals, getCustomTimeseriesIntervalLabel } from './utils/utils'

const DATE_PICKER_TYPE_MAP: Partial<Record<GranularityEnum, DatePickerType>> = {
  [GranularityEnum.FIFTEEN_MINUTES]: 'datetime',
  [GranularityEnum.ONE_HOUR]: 'datetime',
  [GranularityEnum.ONE_DAY]: 'day',
  [GranularityEnum.ONE_WEEK]: 'week',
  [GranularityEnum.ONE_MONTH]: 'month',
}

const getSeriesTypesOptions = (t: (key: string) => string) => [
  { value: 'flowRate', label: t('data_management.series_type_flow_rate') },
  { value: 'amount', label: t('data_management.series_type_amount') },
  { value: 'static', label: t('data_management.series_type_count') },
]

const createGridColumns = (unit: string, t: (key: string) => string): EditableDataGridColumn[] => {
  return [
    { title: t('data_management.time'), id: 'timestamp', readonly: true, format: 'text' },
    {
      title: unit ? `${t('data_management.value')} (${unit})` : `${t('data_management.value')} ()`,
      id: 'value',
      readonly: false,
      format: 'number',
    },
  ]
}

const createGridRows = (
  intervals: string[],
  values: number[],
  granularity: GranularityEnum | '',
  initialData: any[],
  timeseriesDataMap?: Map<string, number>
): EditableDataGridRow[] => {
  const dataMap =
    timeseriesDataMap ?? new Map(initialData.map(d => [getCustomTimeseriesIntervalLabel(new Date(d.timestamp), granularity), d.value]))

  const rows = intervals.map((interval, index) => {
    const initialValue = dataMap.get(interval) || 0

    return {
      id: index.toString(),
      cells: [interval, values[index] || 0],
      initialValue,
    }
  })

  return rows
}

interface IAnalyticsCustomDataDetailsProps {
  selectedSeries: string
  formik: FormikProps<ICustomDataForm>
  onTableSheetValuesChange: (rows: EditableDataGridRow[]) => void
  endDateOverride?: Date
}

const AnalyticsCustomDataDetails = ({
  selectedSeries,
  formik,
  onTableSheetValuesChange,
  endDateOverride,
}: IAnalyticsCustomDataDetailsProps) => {
  const { t } = useTranslation()
  const { pgIdOptions: locationsOptions } = useLocationsContext()

  const GRANULARITY_OPTIONS = [
    { value: GranularityEnum.FIFTEEN_MINUTES, label: getGranularityLabel(GranularityEnum.FIFTEEN_MINUTES) },
    { value: GranularityEnum.ONE_HOUR, label: getGranularityLabel(GranularityEnum.ONE_HOUR) },
    { value: GranularityEnum.ONE_DAY, label: getGranularityLabel(GranularityEnum.ONE_DAY) },
    { value: GranularityEnum.ONE_WEEK, label: getGranularityLabel(GranularityEnum.ONE_WEEK) },
    { value: GranularityEnum.ONE_MONTH, label: getGranularityLabel(GranularityEnum.ONE_MONTH) },
  ]
  const [rows, _setRows] = useState<EditableDataGridRow[]>([])
  const [initialRows, setInitialRows] = useState<EditableDataGridRow[]>([])

  const setRows = newRows => {
    _setRows(newRows)
    onTableSheetValuesChange(newRows)
  }

  const gridColumns = useMemo(() => {
    return createGridColumns(formik.values.unit || '', t)
  }, [formik.values.unit, t])

  const [cellChangesIndex, setCellChangesIndex] = useState<number>(-1)
  const [cellChanges, setCellChanges] = useState<EditableDataGridCellChange[][]>([])
  const { data: customTimeseriesData } = useCustomTimeseriesData({ timeSeriesId: selectedSeries })

  const updateRows = (startDate, endDate, granularity) => {
    const intervals = generateCustomTimeseriesIntervals(startDate, endDate, granularity)
    const timeseriesDataMap = new Map<string, number>(
      customTimeseriesData.data.map(d => [getCustomTimeseriesIntervalLabel(new Date(d.timestamp), granularity), d.value])
    )

    const values = intervals.map(interval => {
      return timeseriesDataMap.get(interval) ?? 0
    })

    const createdRows = createGridRows(intervals, values, granularity, customTimeseriesData.data, timeseriesDataMap)
    setRows(createdRows)
    if (initialRows.length === 0) {
      setInitialRows(createdRows)
    }
  }

  const resetRows = granularity => {
    const intervals = generateCustomTimeseriesIntervals(formik.values.startDate, formik.values.endDate, granularity)
    const values = intervals.map(_ => 0)
    const createdRows = createGridRows(intervals, values, granularity, customTimeseriesData.data)
    setRows(createdRows)
    setInitialRows(createdRows)
  }

  const initializeRows = () => {
    updateRows(formik.values.startDate, formik.values.endDate, formik.values.granularity)
  }

  const updateDateBasedOnGranularity = (granularity: GranularityEnum) => {
    let { startDate, endDate } = formik.values

    if (granularity === GranularityEnum.ONE_DAY) {
      startDate = startOfDay(startDate)
      endDate = endOfDay(endDate)
    } else if (granularity === GranularityEnum.ONE_WEEK) {
      startDate = startOfWeek(startDate, { weekStartsOn: 1 })
      endDate = endOfWeek(endDate, { weekStartsOn: 1 })
    } else if (granularity === GranularityEnum.ONE_MONTH) {
      startDate = startOfMonth(startDate)
      endDate = endOfMonth(endDate)
    }

    handleDateChange('startDate', startDate)
    handleDateChange('endDate', endDate)
  }

  const handleFormChange = (key: keyof ICustomDataForm, value: ICustomDataForm[keyof ICustomDataForm]) => {
    formik.setFieldValue(key, value)
  }

  const handleDateChange = (key: 'startDate' | 'endDate', date: Date) => {
    formik.setFieldValue(key, date)
    updateRows(
      key === 'startDate' ? date : formik.values.startDate,
      key === 'endDate' ? date : formik.values.endDate,
      formik.values.granularity
    )
  }

  const handleGranularityChange = value => {
    handleFormChange('granularity', value)
    updateDateBasedOnGranularity(value)
    resetRows(value)
  }

  const handleCellChanges = (changes: EditableDataGridCellChange[]) => {
    const changesSnapshot: EditableDataGridCellChange[] = []

    for (const change of changes) {
      changesSnapshot.push({
        rowId: change.rowId,
        colId: change.colId,
        oldValue: change.oldValue,
        newValue: change.newValue,
      })
    }

    setCellChanges([...cellChanges.slice(0, cellChangesIndex + 1), changesSnapshot])
    setCellChangesIndex(cellChangesIndex + 1)
  }

  useEffect(() => {
    if (formik.status === 'initialized' && customTimeseriesData.data) {
      initializeRows()
    }
  }, [customTimeseriesData.data, formik.status])

  useEffect(() => {
    if (!selectedSeries) {
      initializeRows()
    }
  }, [])

  // Sync metadata (form values) with formik
  useEffect(() => {
    if (!customTimeseriesData.metadata) {
      formik.resetForm()
      return
    }
    const { title, location_id, unit, granularity, start_date, end_date, details, type, aggregationType } = customTimeseriesData.metadata
    const startDate = start_date ? new Date(start_date) : formik.initialValues.startDate
    const endDate = endDateOverride ?? (end_date ? new Date(end_date) : formik.initialValues.endDate)
    const formValues = {
      title,
      locationId: Number(location_id),
      unit,
      granularity,
      startDate,
      endDate,
      details,
      type,
      aggregationType,
    }
    formik.setValues(formValues)
    formik.setStatus('initialized')
  }, [customTimeseriesData.metadata])

  // if there is only one location option, set it
  useEffect(() => {
    if (locationsOptions.length === 1) formik.setFieldValue('locationId', locationsOptions[0].value)
  }, [locationsOptions.length])

  return (
    <FlexCol maxWidth={'100%'} mt='0px' gap='32px'>
      <FlexRow maxWidth={'100%'} gap='24px'>
        <Grid
          flex={'0 1 480px'}
          minWidth={'340px'}
          paddingX={'24px'}
          paddingY={'16px'}
          backgroundColor={'#F9FAFB'}
          borderRadius={'8px'}
          templateColumns='repeat(1, 1)'
          gap='16px'
        >
          <GridItem colSpan={6}>
            <Text fontSize={'16px'} fontWeight={500} lineHeight={'24px'}>
              {t('data_management.parameter')}
            </Text>
          </GridItem>
          <GridItem colSpan={6}>
            <FormInput
              name='name'
              label={t('data_management.time_series_name')}
              maxLength={35}
              value={formik.values.title}
              onChange={e => handleFormChange('title', e.target.value)}
            />
          </GridItem>

          <GridItem colSpan={3}>
            <FormInput
              name='unit'
              label={t('data_management.unit')}
              maxLength={8}
              value={formik.values.unit}
              onChange={e => handleFormChange('unit', e.target.value)}
            />
          </GridItem>

          <GridItem colSpan={3}>
            <FormSelect
              id='custom-data-granularity'
              name='granularity'
              label={t('data_management.resolution')}
              options={GRANULARITY_OPTIONS}
              value={formik.values.granularity}
              onChange={value => handleGranularityChange(value)}
            />
          </GridItem>

          <GridItem colSpan={3}>
            <FormDatePicker
              pickerType={DATE_PICKER_TYPE_MAP[formik.values.granularity]}
              name='startDate'
              label={t('data_management.period_from')}
              value={formik.values.startDate}
              onChange={date => {
                if (date) handleDateChange('startDate', date)
              }}
            ></FormDatePicker>
          </GridItem>

          <GridItem colSpan={3}>
            <FormDatePicker
              pickerType={DATE_PICKER_TYPE_MAP[formik.values.granularity]}
              name='endDate'
              label={t('data_management.period_to')}
              value={formik.values.endDate}
              onChange={date => {
                if (date) handleDateChange('endDate', date)
              }}
            ></FormDatePicker>
          </GridItem>

          <GridItem colSpan={6}>
            <FormSelect
              id='custom-data-location'
              name='locationId'
              label={t('common.location')}
              options={locationsOptions}
              isDisabled={locationsOptions?.length === 1}
              value={formik.values.locationId}
              onChange={value => handleFormChange('locationId', value)}
            />
          </GridItem>

          <GridItem colSpan={6}>
            <FormInput
              name='input-name'
              label={t('data_management.description')}
              maxLength={400}
              value={formik.values.details}
              onChange={e => handleFormChange('details', e.target.value)}
            />
          </GridItem>

          <GridItem colSpan={6}>
            <FormSelect
              id='custom-data-type'
              name='type'
              label={t('data_management.type')}
              options={getSeriesTypesOptions(t)}
              value={formik.values.aggregationType}
              onChange={value => handleFormChange('aggregationType', value)}
            />
          </GridItem>
        </Grid>

        <Box display='flex' flexDirection='column' flex={'1 1 500px'} minWidth={0}>
          <FlexCol gap={'32px'} marginTop={'12px'} width={'100%'}>
            <Flex justifyContent='space-between'>
              <FlexRow alignItems={'center'} gap='16px' width={'100%'}>
                <Box>
                  <Text fontWeight={600} fontSize={'18px'} lineHeight={'32px'}>
                    {t('data_management.data')}
                  </Text>
                </Box>
                <NumberBox number={rows.length} />
                <FlexRow marginLeft={'auto'}>
                  <PasteHintBadge />
                </FlexRow>
              </FlexRow>
            </Flex>

            <EditableDataGrid
              columns={gridColumns}
              data={rows}
              initialData={initialRows}
              onDataChange={setRows}
              onCellsChanged={handleCellChanges}
              highlightChangedCells={true}
              height={450}
              minColumnWidth={300}
            />
          </FlexCol>
        </Box>
      </FlexRow>
    </FlexCol>
  )
}

export default AnalyticsCustomDataDetails
